import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { U3129ProjectSearchResultsTable } from "./U3129ProjectSearchResultsTable";
import {
  U3129_FIND_PROJECT_DEFAULT_SORT_FIELD,
  U3129_FIND_PROJECT_DEFAULT_SORT_ORDER,
  u3129NextFindProjectSort,
  type U3129FindProjectSortField,
  type U3129FindProjectSortOrder,
} from "../../tools/u3129BuildFindProjectSearchCriterias";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputTextBase from "../../../u2000Core/components/formControls/input/U2000InputTextBase";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000KeyboardKeyHint from "../../../u2000Core/components/common/U2000KeyboardKeyHint";
import U2000AutoCompletePanelResultsBlock, {
  U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS,
} from "../../../u2000Core/components/common/U2000AutoCompletePanelResultsBlock";
import U2000AutoCompleteResultKeyboardHints from "../../../u2000Core/components/common/U2000AutoCompleteResultKeyboardHints";
import {
  createPanelCriteriaFocusHandler,
  U2000_AUTOCOMPLETE_PANEL_COMPACT,
} from "../../../u2000Core/tools/u2000AutocompletePanelUtils";
import { useU3129FindProjectHandlers, useU3129FindSubprojectHandlers } from "../../U3129UseFindProjectSubprojectHandlers";
import type {
  U3129ExpenseItem,
  U3129EquipmentItem,
  U3129FindProjectItem,
  U3129FindSubprojectItem,
  U3129HonorariumItem,
  U3129HonorariumProjectRef,
  U3129Timesheet,
} from "../../U3129Service";
import { U3129_READONLY_TABLE_CELL_BOX_CLASS, U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from "../../tools/u3129ReadonlyTableCellClass";
import {
  U3129_GRID_COMPACT_ROW_CELL_PY,
  U3129_GRID_COMPACT_TABLE_CONTROL_CLASS,
  U3129_HONORARIUM_META_COLUMN_CELL_X,
} from "../../tools/u3129TimesheetGridCompactStyles";
import {
  u3129ClientBlockLabelFromProject,
  u3129ClientDisplayFromFindProject,
  u3129ProjectDisplayLabel,
} from "../../tools/u3129BuildHonorariumLinesFromTasks";
import {
  u3129ProjectWorkNatureFromRow,
  u3129TaskWorkNatureDescriptionForLanguage,
} from "../../tools/u3129HonorariumWorkNatureDisplay";

import { FaSearch } from "react-icons/fa";

export type U3129LineArrayField = "honoraria" | "expenses" | "equipment";

type LineItem = U3129HonorariumItem | U3129ExpenseItem | U3129EquipmentItem;

export type U3129ProjectSearchTriggerKey = "F3" | "Enter";

function isCriteriaFocusInPanel(panelRoot: HTMLDivElement | null): boolean {
  if (!panelRoot) return false;
  const active = document.activeElement;
  if (!active || !panelRoot.contains(active)) return false;
  const tag = active.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return active.getAttribute("role") === "combobox";
}

export type U3129ProjectSearchPanelProps = {
  /** Classes sur le conteneur racine du panneau (ex. `min-h-0 flex-1` dans un dialogue flex). */
  className?: string;
  panelFields?: Record<string, unknown>;
  setPanelFields: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onPanelSearch: (fields: Record<string, unknown>) => void | Promise<void>;
  results?: Record<string, unknown>[];
  totalCount?: number;
  truncated?: boolean;
  setShowPanel: (show: boolean) => void;
  handleRowClickFn?: (item: Record<string, unknown>) => void;
  ns: string;
  /** Classes Tailwind fusionnées sur le conteneur du tableau de résultats (ex. max-h + scroll), ex. dialogue honoraires. */
  resultsTableClassName?: string;
  /** Si défini, Échap appelle ce callback au lieu de fermer le panneau latéral (ex. dialogue modal). */
  onEscape?: () => void;
  /** Clé i18n (namespace `ns`) pour le hint Échap sous la grille (défaut : comportement `common`). */
  escapeHintLabelKey?: string;
  /** Touche qui lance la recherche (défaut F3 ; Entrée dans le dialogue honoraires BL-0668). */
  searchTriggerKey?: U3129ProjectSearchTriggerKey;
};

/** Panneau de recherche projet (F3 ou Entrée selon contexte, tableau résultats) — partagé grille et dialogue U3129. */
export function U3129ProjectSearchPanel({
  className: rootClassName,
  panelFields = {},
  setPanelFields,
  onPanelSearch,
  results = [],
  totalCount,
  truncated: _truncated,
  setShowPanel,
  handleRowClickFn,
  ns,
  resultsTableClassName,
  onEscape,
  escapeHintLabelKey,
  searchTriggerKey = "F3",
}: U3129ProjectSearchPanelProps) {
  const { t } = useTranslation();
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [criteriaFieldFocused, setCriteriaFieldFocused] = useState(false);
  const [sortField, setSortField] = useState<U3129FindProjectSortField>(U3129_FIND_PROJECT_DEFAULT_SORT_FIELD);
  const [sortOrder, setSortOrder] = useState<U3129FindProjectSortOrder>(U3129_FIND_PROJECT_DEFAULT_SORT_ORDER);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const projetNumberRef = useRef<HTMLInputElement | null>(null);
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  const list = Array.isArray(results) ? results : [];
  const resultFields = ["projetNumber", "projetName", "customerCode", "customerName"];

  const runPanelSearch = useCallback(
    (fields: Record<string, unknown>) => {
      const payload = {
        ...fields,
        sortField,
        sortOrder,
      };
      void Promise.resolve(onPanelSearch(payload));
    },
    [onPanelSearch, sortField, sortOrder],
  );

  const handleSortColumn = useCallback(
    (field: U3129FindProjectSortField) => {
      const next = u3129NextFindProjectSort(sortField, sortOrder, field);
      setSortField(next.field);
      setSortOrder(next.order);
      const payload = {
        ...(panelFields as Record<string, unknown>),
        sortField: next.field,
        sortOrder: next.order,
      };
      setPanelFields((prev) => ({ ...prev, sortField: next.field, sortOrder: next.order }));
      void Promise.resolve(onPanelSearch(payload));
    },
    [onPanelSearch, panelFields, setPanelFields, sortField, sortOrder],
  );

  useEffect(() => {
    if (list.length > 0) {
      setShowResults(true);
      setHighlightedIndex(-1);
    } else {
      setShowResults(false);
      setHighlightedIndex(-1);
    }
  }, [list.length]);

  const handleCriteriaFocus = useCallback((e: React.FocusEvent) => {
    createPanelCriteriaFocusHandler(setHighlightedIndex)(e);
    const el = e.target as HTMLElement;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.getAttribute("role") === "combobox") {
      setCriteriaFieldFocused(true);
    }
  }, []);

  const handleCriteriaBlurCapture = useCallback(() => {
    requestAnimationFrame(() => {
      setCriteriaFieldFocused(isCriteriaFocusInPanel(panelRef.current));
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (onEscape) {
          e.preventDefault();
          e.stopPropagation();
          onEscape();
          return;
        }
        setShowPanel(false);
        setShowResults(false);
      }
      if (searchTriggerKey === "F3" && e.key === "F3") {
        e.preventDefault();
        runPanelSearch(panelFields as Record<string, unknown>);
        return;
      }
      if (searchTriggerKey === "Enter" && e.key === "Enter" && isCriteriaFocusInPanel(panelRef.current)) {
        if (highlightedIndexRef.current >= 0) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        runPanelSearch(panelFields as Record<string, unknown>);
      }
    };
    const useCapture = !!onEscape || searchTriggerKey === "Enter";
    window.addEventListener("keydown", handleKeyDown as EventListener, useCapture);
    return () => window.removeEventListener("keydown", handleKeyDown as EventListener, useCapture);
  }, [panelFields, runPanelSearch, setShowPanel, onEscape, searchTriggerKey]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (projetNumberRef.current) {
        try {
          projetNumberRef.current.focus();
        } catch {
          // ignore
        }
      }
      if (searchTriggerKey === "Enter") {
        setCriteriaFieldFocused(isCriteriaFocusInPanel(panelRef.current));
      }
    });
  }, [searchTriggerKey]);

  const getFocusableElements = () => {
    if (!panelRef.current) return [] as HTMLElement[];
    const selectors = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", '[tabindex]:not([tabindex="-1"])'];
    return Array.from(panelRef.current.querySelectorAll(selectors.join(","))) as HTMLElement[];
  };

  const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const focusable = getFocusableElements().filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!panelRef.current?.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  };

  const handleRowClick = (item: Record<string, unknown>, idx: number) => {
    setShowPanel(false);
    setHighlightedIndex(-1);
    void idx;
    handleRowClickFn?.(item);
  };

  const showSearchEnterHint = searchTriggerKey === "Enter" && criteriaFieldFocused && highlightedIndex < 0;

  return (
    <div
      ref={panelRef}
      onKeyDown={handleTrapKeyDown}
      className={`flex flex-col ${U2000_AUTOCOMPLETE_PANEL_COMPACT.rootGap} rounded-lg border border-gray-200 bg-white p-4 shadow-lg animate-fade-in ${resultsTableClassName ? "min-h-0 flex-1" : "h-full"} ${rootClassName ?? ""}`}
      tabIndex={0}
    >
      <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaToolbar}>
        <div
          className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaFields}
          onFocusCapture={handleCriteriaFocus}
          onBlurCapture={handleCriteriaBlurCapture}
        >
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
            <div className="w-[140px] shrink-0">
              <U2000InputTextBase
                placeholder={t("lblProject", { ns, defaultValue: "Projet" })}
                fieldName="projetNumber"
                type="string"
                value={(panelFields.projetNumber as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, projetNumber: value ?? "" }))}
                inputRef={projetNumberRef}
              />
            </div>
            <div className="min-w-0 flex-2" style={{ minWidth: 180 }}>
              <U2000InputTextBase
                placeholder={t("lblProjetName", { ns, defaultValue: "Description projet" })}
                fieldName="projetName"
                type="string"
                value={(panelFields.projetName as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, projetName: value ?? "" }))}
              />
            </div>
          </div>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
            <div className="w-[140px] shrink-0">
              <U2000InputTextBase
                placeholder={t("lblCustomerCode", { ns, defaultValue: "Code client" })}
                fieldName="customerCode"
                type="string"
                value={(panelFields.customerCode as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, customerCode: value ?? "" }))}
              />
            </div>
            <div className="min-w-0 flex-1" style={{ minWidth: 120 }}>
              <U2000InputTextBase
                placeholder={t("lblCustomerName", { ns, defaultValue: "Nom client" })}
                fieldName="customerName"
                type="string"
                value={(panelFields.customerName as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, customerName: value ?? "" }))}
              />
            </div>
          </div>
        </div>
        <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.searchRow}>
          <U2000ActionBtn
            className="w-full"
            label={t("btnSearch", { ns: "common", defaultValue: "Rechercher" })}
            icon={FaSearch}
            trailingHint={
              searchTriggerKey === "Enter" ? (
                showSearchEnterHint ? (
                  <U2000KeyboardKeyHint>{t("keyboardHintEnterKey", { ns: "common", defaultValue: "Entrée" })}</U2000KeyboardKeyHint>
                ) : null
              ) : (
                <U2000KeyboardKeyHint>F3</U2000KeyboardKeyHint>
              )
            }
            handleClick={() => runPanelSearch(panelFields as Record<string, unknown>)}
          />
        </div>
      </div>
      {resultsTableClassName ? (
        <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden">
          <U3129ProjectSearchResultsTable
            results={list}
            resultFields={resultFields}
            handleRowClick={handleRowClick}
            showResults={showResults}
            setShowResults={setShowResults}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            className={resultsTableClassName}
            totalCount={totalCount}
            ns={ns}
            activeSortField={sortField}
            activeSortOrder={sortOrder}
            onSort={handleSortColumn}
          />
          <U2000AutoCompleteResultKeyboardHints
            highlightedIndex={highlightedIndex}
            escapeHintNs={escapeHintLabelKey ? ns : undefined}
            escapeHintLabelKey={escapeHintLabelKey}
          />
        </div>
      ) : (
        <U2000AutoCompletePanelResultsBlock highlightedIndex={highlightedIndex}>
          <U3129ProjectSearchResultsTable
            results={list}
            resultFields={resultFields}
            handleRowClick={handleRowClick}
            showResults={showResults}
            setShowResults={setShowResults}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            className={U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS}
            totalCount={totalCount}
            ns={ns}
            activeSortField={sortField}
            activeSortOrder={sortOrder}
            onSort={handleSortColumn}
          />
        </U2000AutoCompletePanelResultsBlock>
      )}
    </div>
  );
}

export interface U3129LineProjectSubprojectRowCellsProps {
  baseField: string;
  arrayField: U3129LineArrayField;
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
  findProjectHandlers: ReturnType<typeof useU3129FindProjectHandlers>;
  getProjectId: () => string | undefined | null;
  readonly: boolean;
  rowIncludeSubProject: boolean;
  /** Personnalisation initLU `admIncludeSubProject === "true"` : affiche la colonne sous-projet. */
  showSubProjectColumn: boolean;
  /** Si true, réinitialise taskType / task (honoraires) lors d’un changement de projet ou sous-projet. */
  clearTaskFieldsOnProjectChange: boolean;
  /** Si true, champs projet / sous-projet en lecture seule (ex. tâche déjà fixée sur la ligne). */
  lockProjectAndSubproject?: boolean;
  /** Grille honoraires compacte (BL-0672) : projet / client / sous-projet alignés sur h-8. */
  compactTableControls?: boolean;
  /** Honoraires : projet en lecture seule (champ texte comme client), sélection via dialogue Ajouter. */
  projectReadOnlyLikeClient?: boolean;
  /** Honoraires responsive : largeurs colonnes métier (sinon défauts onglets dépense/équipement). */
  honorariumMetaColClasses?: {
    project?: string;
    customer?: string;
    natures?: string;
    subproject?: string;
  };
  ns: string;
}

/** Cellules tableau : projet, client (lecture seule), et optionnellement sous-projet — aligné sur l’onglet Honoraires. */
export function U3129LineProjectSubprojectRowCells({
  baseField,
  arrayField,
  index,
  formStateHandlers,
  findProjectHandlers,
  getProjectId,
  readonly,
  rowIncludeSubProject,
  showSubProjectColumn,
  clearTaskFieldsOnProjectChange,
  lockProjectAndSubproject = false,
  compactTableControls = false,
  projectReadOnlyLikeClient = false,
  honorariumMetaColClasses,
  ns,
}: U3129LineProjectSubprojectRowCellsProps) {
  const readonlyBoxClass = compactTableControls ? U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS : U3129_READONLY_TABLE_CELL_BOX_CLASS;
  const controlCellPy = compactTableControls ? U3129_GRID_COMPACT_ROW_CELL_PY : "py-2";
  const controlWrapClass = compactTableControls
    ? `text-left min-w-0 ${U3129_GRID_COMPACT_TABLE_CONTROL_CLASS} [&_input]:text-left`
    : "text-left [&_input]:text-left min-w-0";
  const clientAsMutedDisplayBox = projectReadOnlyLikeClient || compactTableControls;
  const projectColClass =
    honorariumMetaColClasses?.project ??
    (compactTableControls && !projectReadOnlyLikeClient ? "w-48 min-w-48" : "w-40 min-w-40");
  const customerColClass = honorariumMetaColClasses?.customer ?? "min-w-88";
  const naturesColClass = honorariumMetaColClasses?.natures;
  const subprojectColClass = honorariumMetaColClasses?.subproject ?? "w-48 min-w-48";
  const honorariumTruncateClass = honorariumMetaColClasses ? " min-w-0 truncate" : "";
  const metaCellX = honorariumMetaColClasses ? U3129_HONORARIUM_META_COLUMN_CELL_X : "px-3";
  const { i18n } = useTranslation(ns);
  const projectCellX = honorariumMetaColClasses ? U3129_HONORARIUM_META_COLUMN_CELL_X : "pl-1 pr-3";

  const findSubprojectHandlers = useU3129FindSubprojectHandlers({ getProjectId });

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      return findProjectHandlers.search(criterias);
    },
    [findProjectHandlers],
  );

  const onSearchSubproject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const results = await findSubprojectHandlers.search(criterias);
      const list = Array.isArray(results) ? results : results.results;
      return list.map((r: U3129FindSubprojectItem) => ({ value: r.id, ...r }));
    },
    [findSubprojectHandlers],
  );

  const extractProject = useCallback(
    (result: U3129FindProjectItem | null): { fieldName: string; value: unknown }[] => {
      const clientDisplayValue = result ? u3129ClientDisplayFromFindProject(result) : "";
      const taskClears: { fieldName: string; value: unknown }[] = clearTaskFieldsOnProjectChange
        ? [
            { fieldName: `${baseField}.taskType`, value: "" },
            { fieldName: `${baseField}.task`, value: null },
          ]
        : [];
      if (result) {
        return [
          { fieldName: `${baseField}.project`, value: result },
          { fieldName: `${baseField}.projectId`, value: result.id },
          { fieldName: `${baseField}.projectDisplay`, value: u3129ProjectDisplayLabel(result, result.id) },
          { fieldName: `${baseField}.clientDisplay`, value: clientDisplayValue },
          { fieldName: `${baseField}.subprojectId`, value: null },
          { fieldName: `${baseField}.subproject`, value: null },
          ...taskClears,
        ];
      }
      return [
        { fieldName: `${baseField}.project`, value: null },
        { fieldName: `${baseField}.projectId`, value: null },
        { fieldName: `${baseField}.projectDisplay`, value: "" },
        { fieldName: `${baseField}.clientDisplay`, value: "" },
        { fieldName: `${baseField}.subprojectId`, value: null },
        { fieldName: `${baseField}.subproject`, value: null },
        ...taskClears,
      ];
    },
    [baseField, clearTaskFieldsOnProjectChange],
  );

  const extractSubproject = useCallback(
    (result: U3129FindSubprojectItem | null): { fieldName: string; value: unknown }[] => {
      const taskClears: { fieldName: string; value: unknown }[] = clearTaskFieldsOnProjectChange
        ? [
            { fieldName: `${baseField}.taskType`, value: "" },
            { fieldName: `${baseField}.task`, value: null },
          ]
        : [];
      if (result) {
        return [
          { fieldName: `${baseField}.subproject`, value: result },
          { fieldName: `${baseField}.subprojectId`, value: result.id },
          ...taskClears,
        ];
      }
      return [
        { fieldName: `${baseField}.subproject`, value: null },
        { fieldName: `${baseField}.subprojectId`, value: null },
        ...taskClears,
      ];
    },
    [baseField, clearTaskFieldsOnProjectChange],
  );

  const data = formStateHandlers.getData();
  const row = data?.[arrayField]?.[index] as LineItem | undefined;
  const rowRec = row as unknown as Record<string, unknown> | undefined;
  const projectDisplay = rowRec?.project as U3129FindProjectItem | U3129HonorariumProjectRef | undefined;
  const subprojectDisplay = rowRec?.subproject as U3129FindSubprojectItem | undefined;
  const shouldShowSubproject = rowIncludeSubProject;

  const projectRef = row?.project as U3129HonorariumProjectRef | U3129FindProjectItem | undefined;
  const findProj = projectDisplay && "customerName" in projectDisplay ? (projectDisplay as U3129FindProjectItem) : undefined;
  const clientFromProject = u3129ClientBlockLabelFromProject(
    (projectRef ?? findProj) as U3129FindProjectItem | U3129HonorariumProjectRef | undefined,
  );
  const rawClientDisplay = rowRec?.clientDisplay;
  const clientDisplayValue =
    clientFromProject ||
    (rawClientDisplay != null && String(rawClientDisplay).trim() !== "" ? String(rawClientDisplay).trim() : "") ||
    "—";
  const clientDisplayFallback = clientFromProject || "—";

  const honorariumRow = arrayField === "honoraria" ? (row as U3129HonorariumItem | undefined) : undefined;
  const honorariumNatureLabel =
    honorariumMetaColClasses && honorariumRow
      ? honorariumRow.taskType?.trim()
        ? u3129TaskWorkNatureDescriptionForLanguage(honorariumRow, i18n.language)
        : u3129ProjectWorkNatureFromRow(honorariumRow)
      : "";
  const honorariumNatureBoxClass =
    honorariumRow?.taskType?.trim()
      ? `${readonlyBoxClass} h-full min-h-8 w-full break-words whitespace-normal leading-tight`
      : `${readonlyBoxClass}${honorariumTruncateClass} tabular-nums`;
  const honorariumNaturesCell =
    honorariumMetaColClasses && arrayField === "honoraria" ? (
      <td className={`${metaCellX} ${controlCellPy} align-top text-left ${naturesColClass ?? ""}`}>
        <div className={honorariumNatureBoxClass} title={honorariumNatureLabel || undefined}>
          {honorariumNatureLabel}
        </div>
      </td>
    ) : null;

  const projectDisplayFallback = u3129ProjectDisplayLabel(projectDisplay ?? null, row?.projectId);
  const rawProjectDisplay = rowRec?.projectDisplay;
  const projectDisplayValue =
    rawProjectDisplay != null && String(rawProjectDisplay).trim() !== ""
      ? String(rawProjectDisplay)
      : projectDisplayFallback || "—";

  useEffect(() => {
    if (readonly || !projectReadOnlyLikeClient) return;
    if (!projectDisplayFallback) return;
    if (rawProjectDisplay != null && String(rawProjectDisplay).trim() !== "") return;
    const currentData = formStateHandlers.getData();
    const list = (currentData?.[arrayField] ?? []) as LineItem[];
    const currentRow = list[index];
    if (!currentRow) return;
    const updated = list.map((r, i) =>
      i === index ? ({ ...(r as unknown as Record<string, unknown>), projectDisplay: projectDisplayFallback } as LineItem) : r,
    );
    formStateHandlers.setData({ ...currentData, [arrayField]: updated } as U3129Timesheet);
  }, [readonly, projectReadOnlyLikeClient, projectDisplayFallback, rawProjectDisplay, formStateHandlers, index, arrayField]);

  useEffect(() => {
    if (readonly) return;
    if (!clientDisplayFallback || clientDisplayFallback === "—") return;
    if (rawClientDisplay != null && String(rawClientDisplay).trim() !== "") return;
    const currentData = formStateHandlers.getData();
    const list = (currentData?.[arrayField] ?? []) as LineItem[];
    const currentRow = list[index];
    if (!currentRow) return;
    const currentValue = (currentRow as unknown as Record<string, unknown>).clientDisplay;
    if (currentValue != null && String(currentValue).trim() !== "") return;
    const updated = list.map((r, i) =>
      i === index ? ({ ...(r as unknown as Record<string, unknown>), clientDisplay: clientDisplayFallback } as LineItem) : r
    );
    formStateHandlers.setData({ ...currentData, [arrayField]: updated } as U3129Timesheet);
  }, [readonly, clientDisplayFallback, rawClientDisplay, formStateHandlers, index, arrayField]);

  if (readonly) {
    return (
      <>
        <td className={`${projectCellX} ${controlCellPy} align-top text-left ${projectColClass}`}>
          <div className={`${readonlyBoxClass}${honorariumTruncateClass}`}>
            {projectDisplay ? (projectDisplay.projetNumber ?? projectDisplay.id) : (row?.projectId ?? "—")}
          </div>
        </td>
        <td className={`${metaCellX} ${controlCellPy} align-top text-left ${customerColClass}`}>
          <div className={`${readonlyBoxClass}${honorariumTruncateClass}`}>
            {clientDisplayValue != null && String(clientDisplayValue).trim() ? String(clientDisplayValue) : "—"}
          </div>
        </td>
        {honorariumNaturesCell}
        {showSubProjectColumn ? (
          <td className={`${metaCellX} ${controlCellPy} align-top text-left ${subprojectColClass}`}>
            <div className={readonlyBoxClass}>
              {shouldShowSubproject
                ? subprojectDisplay
                  ? `${subprojectDisplay.subProjetNumber ?? ""} ${subprojectDisplay.subProjetName ?? ""}`.trim() || subprojectDisplay.id
                  : (row?.subprojectId ?? "—")
                : "—"}
            </div>
          </td>
        ) : null}
      </>
    );
  }

  return (
    <>
      <td className={`${projectCellX} ${controlCellPy} align-top text-left ${projectColClass}`}>
        <div className={projectReadOnlyLikeClient ? "min-w-0" : controlWrapClass}>
          {projectReadOnlyLikeClient ? (
            <div className={readonlyBoxClass}>{projectDisplayValue}</div>
          ) : (
            <U2000AutoCompleteInput
              pageSize={15}
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.project`}
              mainFieldName="projetNumber"
              entityLink="U3121"
              label=""
              onSearch={onSearchProject}
              displayFn={(item) => {
                const p = item as U3129FindProjectItem;
                return p ? (p.projetNumber ?? p.id) : "";
              }}
              overrideExtractDataFn={(item) => extractProject(item as U3129FindProjectItem | null)}
              basicResultHeight={300}
              resultLabels={[]}
              resultFields={["projetNumber", "projetName"]}
              minCarLaunchSearch={3}
              customSearchPanelHeight={500}
              panelWidth={400}
              dropdownWidth={400}
              renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns={ns} />}
              readonly={readonly || lockProjectAndSubproject}
              compactGrid={compactTableControls}
            />
          )}
        </div>
      </td>
      <td className={`${metaCellX} ${controlCellPy} align-top text-left ${customerColClass}`}>
        <div className={clientAsMutedDisplayBox ? "min-w-0" : controlWrapClass}>
          {clientAsMutedDisplayBox ? (
            <div className={`${readonlyBoxClass}${honorariumTruncateClass}`}>
              {clientDisplayValue != null && String(clientDisplayValue).trim() ? String(clientDisplayValue) : "—"}
            </div>
          ) : (
            <U2000InputText
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.clientDisplay`}
              label=""
              readonly={readonly || lockProjectAndSubproject}
              required={false}
            />
          )}
        </div>
      </td>
      {honorariumNaturesCell}
      {showSubProjectColumn ? (
        <td className={`px-3 ${controlCellPy} align-top w-48 text-left min-w-48`}>
          <div className={controlWrapClass}>
            <U2000AutoCompleteInput
              pageSize={15}
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.subproject`}
              mainFieldName="subProjetNumber"
              label=""
              onSearch={onSearchSubproject}
              displayFn={(item) => {
                const sp = item as U3129FindSubprojectItem;
                return sp ? sp.subProjetNumber?.trim() || sp.id : "";
              }}
              overrideExtractDataFn={(item) => extractSubproject(item as U3129FindSubprojectItem | null)}
              basicResultHeight={300}
              resultLabels={[]}
              resultFields={["subProjetNumber", "subProjetName"]}
              minCarLaunchSearch={0}
              readonly={readonly || lockProjectAndSubproject || !getProjectId() || !rowIncludeSubProject}
              compactGrid={compactTableControls}
            />
          </div>
        </td>
      ) : null}
    </>
  );
}
